import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import StackedLineChartRoundedIcon from '@mui/icons-material/StackedLineChartRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import TopicRoundedIcon from '@mui/icons-material/TopicRounded';
import { exportToPdf, getHistoryById } from '../services/historyService';
import { getScoreColorHex } from '../utils/scoreUtils';
import { getMediaUrl } from '../utils/mediaUrl';
import { marketingPalette, marketingSymbols } from '../content/marketing';
import {
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
} from '../components/common/PublicPageFrame';

const palette = marketingPalette;

const surfaceSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: { xs: '26px', md: '30px' },
  border: `1px solid ${alpha(palette.ink, 0.08)}`,
  bgcolor: alpha('#ffffff', 0.94),
  boxShadow: '0 28px 72px rgba(18, 61, 103, 0.08)',
};

const tableContainerSx = {
  borderRadius: '22px',
  border: `1px solid ${alpha(palette.ink, 0.08)}`,
  overflowX: 'auto',
  boxShadow: 'none',
};

const tableHeaderCellSx = {
  bgcolor: palette.heroBlueMist,
  borderBottom: `1px solid ${alpha(palette.ink, 0.08)}`,
  color: palette.ink,
  fontFamily: publicBodyFontFamily,
  fontWeight: 800,
  whiteSpace: 'nowrap',
};

const tableBodyCellSx = {
  verticalAlign: 'top',
  borderBottom: `1px solid ${alpha(palette.ink, 0.06)}`,
  fontFamily: publicBodyFontFamily,
};

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function formatDate(dateString) {
  if (!dateString) return 'Date unavailable';

  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDurationMinutes(minutes) {
  const numeric = Number(minutes);
  if (!Number.isFinite(numeric)) return null;
  return Math.round(numeric);
}

function buildOrderedSections(history) {
  const snapshotSections = Array.isArray(history?.caseSnapshot?.sections)
    ? [...history.caseSnapshot.sections]
    : [];
  const questions = Array.isArray(history?.historyQuestions) ? history.historyQuestions : [];

  return snapshotSections
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
    .map((section) => ({
      key: section._id || section.id || section.sectionType || section.title,
      type: section.sectionType,
      title: section.title || 'Untitled section',
      questions: questions.filter((question) => (
        normalizeText(question.sectionType) === normalizeText(section.sectionType)
        || normalizeText(question.sectionTitle) === normalizeText(section.title)
      )),
    }))
    .filter((section) => section.questions.length > 0);
}

function hasAssessmentContent(entry) {
  return Boolean(
    entry
    && (
      Number(entry.maxPoints) > 0
      || Number(entry.score) > 0
      || String(entry.feedback || '').trim()
    )
  );
}

function hasFlowAssessmentContent(flowAssessment) {
  return Boolean(
    flowAssessment
    && [
      flowAssessment.logicalSequencing,
      flowAssessment.appropriatePhrasing,
      flowAssessment.avoidanceMedicalJargon,
    ].some(hasAssessmentContent)
  );
}

function MetricCard({ icon, label, value }) {
  const valueColor = getScoreColorHex(value || 0);

  return (
    <Box
      sx={{
        ...surfaceSx,
        p: 2.4,
        height: '100%',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography
            sx={{
              color: palette.inkSoft,
              fontSize: '0.82rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: publicBodyFontFamily,
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              mt: 0.9,
              color: valueColor,
              fontSize: { xs: '1.8rem', md: '2rem' },
              lineHeight: 1,
              fontWeight: 900,
              fontFamily: publicHeadingFontFamily,
              letterSpacing: '-0.04em',
            }}
          >
            {(Number(value) || 0).toFixed(1)}%
          </Typography>
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '18px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(valueColor, 0.12),
            color: valueColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Box>
  );
}

function ScoreChip({ score, maxPoints }) {
  const resolvedScore = Number(score) || 0;
  const resolvedMax = Number(maxPoints) || 0;
  const resolvedColor = getScoreColorHex(resolvedScore, resolvedMax || 1);

  return (
    <Chip
      label={`${resolvedScore}/${resolvedMax}`}
      size="small"
      sx={{
        minWidth: 64,
        bgcolor: alpha(resolvedColor, 0.14),
        color: resolvedColor,
        borderRadius: '999px',
        fontFamily: publicBodyFontFamily,
        fontWeight: 800,
      }}
    />
  );
}

function EmptySectionState({ message }) {
  return (
    <Box
      sx={{
        borderRadius: '22px',
        border: `1px dashed ${alpha(palette.ink, 0.14)}`,
        bgcolor: alpha(palette.heroBlueMist, 0.75),
        py: 5,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Typography
        sx={{
          color: palette.inkSoft,
          lineHeight: 1.7,
          fontFamily: publicBodyFontFamily,
        }}
      >
        {message}
      </Typography>
    </Box>
  );
}

function MediaViewerDialog({ open, onClose, src, title, mimetype }) {
  const isImage = mimetype?.startsWith('image/');
  const isPdf = mimetype === 'application/pdf';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          m: 2,
          borderRadius: '26px',
          backgroundColor: 'rgba(8, 20, 35, 0.96)',
          boxShadow: '0 36px 90px rgba(0, 0, 0, 0.36)',
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              color: palette.white,
              fontWeight: 800,
              fontFamily: publicBodyFontFamily,
              wordBreak: 'break-word',
            }}
          >
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: palette.white,
              bgcolor: alpha('#ffffff', 0.10),
              '&:hover': {
                bgcolor: alpha('#ffffff', 0.18),
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isImage ? (
            <Box
              component="img"
              src={src}
              alt={title}
              sx={{
                maxWidth: '100%',
                maxHeight: '72vh',
                objectFit: 'contain',
                borderRadius: '18px',
              }}
            />
          ) : isPdf ? (
            <Box
              component="iframe"
              src={src}
              title={title}
              sx={{
                width: '100%',
                height: '72vh',
                border: 'none',
                borderRadius: '18px',
                bgcolor: '#ffffff',
              }}
            />
          ) : (
            <Stack alignItems="center" spacing={2}>
              <AttachFileRoundedIcon sx={{ fontSize: 60, color: alpha('#ffffff', 0.76) }} />
              <Button
                component="a"
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewRoundedIcon />}
                sx={{
                  ...publicPrimaryButtonSx,
                  bgcolor: palette.white,
                  color: palette.heroBlueDeep,
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.90),
                  },
                }}
              >
                Open file
              </Button>
            </Stack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function QuestionAttachments({ question, onOpen }) {
  if (!Array.isArray(question.mediaFiles) || question.mediaFiles.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
      {question.mediaFiles.map((file, index) => {
        const url = getMediaUrl(file) || getMediaUrl(file?.path) || '';
        if (!url) {
          return null;
        }

        const label = file?.originalName || file?.filename || `Attachment ${index + 1}`;
        const isPdf = file?.mimetype === 'application/pdf';
        const isImage = file?.mimetype?.startsWith('image/');
        const icon = isPdf
          ? <PictureAsPdfRoundedIcon sx={{ fontSize: 16 }} />
          : isImage
            ? <ImageOutlinedIcon sx={{ fontSize: 16 }} />
            : <AttachFileRoundedIcon sx={{ fontSize: 16 }} />;

        return (
          <Button
            key={file?.url || file?.path || `${label}-${index}`}
            onClick={() => onOpen(url, label, file?.mimetype || '')}
            startIcon={icon}
            sx={{
              minHeight: 34,
              px: 1.5,
              borderRadius: '999px',
              bgcolor: alpha(palette.heroBlueMid, 0.08),
              color: palette.heroBlueDeep,
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: publicBodyFontFamily,
              '&:hover': {
                bgcolor: alpha(palette.heroBlueMid, 0.14),
              },
            }}
          >
            {label}
          </Button>
        );
      })}
    </Stack>
  );
}

function HistoryQuestionsTable({ questions }) {
  if (!questions.length) {
    return <EmptySectionState message="No scored topics were recorded for this section." />;
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
      <Table sx={{ minWidth: 860 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tableHeaderCellSx, width: '24%' }}>Topic</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '34%' }}>Marking criteria</TableCell>
            <TableCell align="center" sx={{ ...tableHeaderCellSx, width: '12%' }}>Score</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '30%' }}>Feedback</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {questions.map((question, index) => (
            <TableRow
              key={question.id || question._id || `${question.topic}-${index}`}
              sx={{
                '&:nth-of-type(even)': {
                  bgcolor: alpha(palette.heroBlueMist, 0.34),
                },
              }}
            >
              <TableCell sx={tableBodyCellSx}>
                <Typography
                  sx={{
                    color: palette.ink,
                    fontWeight: 800,
                    lineHeight: 1.45,
                    fontFamily: publicBodyFontFamily,
                  }}
                >
                  {question.topic || 'Untitled topic'}
                </Typography>
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography
                  sx={{
                    color: palette.inkSoft,
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.7,
                    fontFamily: publicBodyFontFamily,
                  }}
                >
                  {question.markingCriteria || 'No criteria available.'}
                </Typography>
              </TableCell>
              <TableCell align="center" sx={tableBodyCellSx}>
                <ScoreChip score={question.score} maxPoints={question.maxPoints} />
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography
                  sx={{
                    color: question.feedback ? palette.ink : palette.inkSoft,
                    fontStyle: question.feedback ? 'normal' : 'italic',
                    lineHeight: 1.7,
                    fontFamily: publicBodyFontFamily,
                  }}
                >
                  {question.feedback || 'No feedback recorded.'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ExaminerQuestionsTable({ questions, onOpenMedia }) {
  if (!questions.length) {
    return <EmptySectionState message="No examiner questions were attached to this case." />;
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
      <Table sx={{ minWidth: 980 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tableHeaderCellSx, width: '28%' }}>Question</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '19%' }}>Correct answer</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '19%' }}>Your answer</TableCell>
            <TableCell align="center" sx={{ ...tableHeaderCellSx, width: '12%' }}>Score</TableCell>
            <TableCell sx={{ ...tableHeaderCellSx, width: '22%' }}>Feedback</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {questions.map((question, index) => (
            <TableRow
              key={question.id || question._id || `${question.question}-${index}`}
              sx={{
                '&:nth-of-type(even)': {
                  bgcolor: alpha(palette.heroBlueMist, 0.34),
                },
              }}
            >
              <TableCell sx={tableBodyCellSx}>
                <Typography
                  sx={{
                    color: palette.ink,
                    fontWeight: 800,
                    lineHeight: 1.55,
                    fontFamily: publicBodyFontFamily,
                  }}
                >
                  {question.question}
                </Typography>
                <QuestionAttachments question={question} onOpen={onOpenMedia} />
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography sx={{ color: palette.inkSoft, lineHeight: 1.7, fontFamily: publicBodyFontFamily }}>
                  {question.correctAnswer || 'Not provided'}
                </Typography>
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography sx={{ color: palette.inkSoft, lineHeight: 1.7, fontFamily: publicBodyFontFamily }}>
                  {question.studentAnswer || 'Not provided'}
                </Typography>
              </TableCell>
              <TableCell align="center" sx={tableBodyCellSx}>
                <ScoreChip score={question.score} maxPoints={question.maxPoints} />
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography sx={{ color: palette.inkSoft, lineHeight: 1.7, fontFamily: publicBodyFontFamily }}>
                  {question.feedback || 'No feedback recorded.'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function FlowAssessmentTable({ flowAssessment }) {
  const rows = [
    { label: 'Logical sequencing', entry: flowAssessment?.logicalSequencing },
    { label: 'Appropriate phrasing', entry: flowAssessment?.appropriatePhrasing },
    { label: 'Avoidance of medical jargon', entry: flowAssessment?.avoidanceMedicalJargon },
  ].filter((row) => row.entry);

  if (!rows.length) {
    return <EmptySectionState message="No communication-flow scoring is available for this case." />;
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
      <Table sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tableHeaderCellSx, width: '28%' }}>Aspect</TableCell>
            <TableCell align="center" sx={{ ...tableHeaderCellSx, width: '14%' }}>Score</TableCell>
            <TableCell sx={tableHeaderCellSx}>Feedback</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell sx={tableBodyCellSx}>
                <Typography sx={{ color: palette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                  {row.label}
                </Typography>
              </TableCell>
              <TableCell align="center" sx={tableBodyCellSx}>
                <ScoreChip score={row.entry?.score} maxPoints={row.entry?.maxPoints} />
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <Typography sx={{ color: palette.inkSoft, lineHeight: 1.7, fontFamily: publicBodyFontFamily }}>
                  {row.entry?.feedback || 'No feedback recorded.'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SectionCard({ title, description, eyebrow, children, action }) {
  return (
    <Box sx={{ ...surfaceSx, p: { xs: 2.5, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ md: 'flex-start' }}
        sx={{ mb: 2.5 }}
      >
        <Box sx={{ maxWidth: 760 }}>
          {eyebrow ? (
            <Typography
              sx={{
                mb: 0.8,
                color: palette.heroBlueMid,
                fontSize: '0.8rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontFamily: publicBodyFontFamily,
              }}
            >
              {eyebrow}
            </Typography>
          ) : null}
          <Typography
            sx={{
              color: palette.ink,
              fontSize: { xs: '1.35rem', md: '1.6rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.03em',
              fontFamily: publicHeadingFontFamily,
            }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              sx={{
                mt: 0.8,
                color: palette.inkSoft,
                lineHeight: 1.72,
                fontFamily: publicBodyFontFamily,
              }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

function HistoryDetailPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [mediaDialog, setMediaDialog] = useState({
    open: false,
    src: '',
    title: '',
    mimetype: '',
  });

  useEffect(() => {
    let active = true;

    const fetchHistory = async () => {
      try {
        setLoading(true);
        setLoadError('');
        const response = await getHistoryById(id);

        if (!active) {
          return;
        }

        setHistory(response || null);
      } catch (error) {
        console.error('Error fetching case details:', error);
        if (active) {
          setLoadError('Failed to load this case. Please try again.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (id) {
      fetchHistory();
    }

    return () => {
      active = false;
    };
  }, [id]);

  const clinicalSections = useMemo(() => buildOrderedSections(history), [history]);
  const learningResources = history?.caseSnapshot?.learning;
  const hasLearningContent = Boolean(
    learningResources
    && (
      learningResources.summary?.trim()
      || (Array.isArray(learningResources.links) && learningResources.links.length > 0)
      || (Array.isArray(learningResources.attachments) && learningResources.attachments.length > 0)
    )
  );
  const hasExaminerQuestions = Array.isArray(history?.examinerQuestions) && history.examinerQuestions.length > 0;
  const showFlowAssessment = hasFlowAssessmentContent(history?.flowAssessment);
  const showEmpathyAssessment = hasAssessmentContent(history?.empathyAssessment);

  useEffect(() => {
    if (selectedTab > clinicalSections.length - 1) {
      setSelectedTab(0);
    }
  }, [clinicalSections.length, selectedTab]);

  const selectedSection = clinicalSections[selectedTab] || null;
  const duration = formatDurationMinutes(history?.duration);
  const isLearnMode = history?.sessionMode === 'learn';
  const sessionModeLabel = isLearnMode ? 'Learn mode' : 'Exam mode';

  const scoreCards = useMemo(() => {
    const cards = [];

    if (history?.categoryScores?.historyQuestions !== undefined) {
      cards.push({
        key: 'history-questions',
        label: 'Clinical history',
        value: history.categoryScores.historyQuestions,
        icon: <TopicRoundedIcon />,
      });
    }

    if (showFlowAssessment && history?.categoryScores?.flow !== undefined) {
      cards.push({
        key: 'flow',
        label: 'Communication flow',
        value: history.categoryScores.flow,
        icon: <StackedLineChartRoundedIcon />,
      });
    }

    if (hasExaminerQuestions && history?.categoryScores?.examinerQuestions !== undefined) {
      cards.push({
        key: 'examiner',
        label: 'Examiner questions',
        value: history.categoryScores.examinerQuestions,
        icon: <SchoolRoundedIcon />,
      });
    }

    if (showEmpathyAssessment && history?.categoryScores?.empathy !== undefined) {
      cards.push({
        key: 'empathy',
        label: 'Empathy',
        value: history.categoryScores.empathy,
        icon: <CheckCircleOutlineRoundedIcon />,
      });
    }

    return cards;
  }, [hasExaminerQuestions, history, showEmpathyAssessment, showFlowAssessment]);

  const handleExportPdf = async () => {
    try {
      setActionError('');
      setExporting(true);
      await exportToPdf(id);
    } catch (error) {
      console.error('Error exporting case PDF:', error);
      if (error.response?.status === 402) {
        setActionError('You need additional credits to export this PDF.');
      } else {
        setActionError('Failed to export the PDF. Please try again.');
      }
    } finally {
      setExporting(false);
    }
  };

  const handleOpenMedia = (src, title, mimetype) => {
    setMediaDialog({
      open: true,
      src,
      title,
      mimetype,
    });
  };

  const handleCloseMedia = () => {
    setMediaDialog({
      open: false,
      src: '',
      title: '',
      mimetype: '',
    });
  };

  const resolveAttachmentUrl = (file) => file?.url || getMediaUrl(file?.path) || '';

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Box
          sx={{
            ...surfaceSx,
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: palette.heroBlueMid }} />
          <Typography sx={{ color: palette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Loading case details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: '20px',
            border: `1px solid ${alpha('#b42318', 0.12)}`,
          }}
        >
          {loadError}
        </Alert>
        <Button
          onClick={() => window.location.reload()}
          sx={{
            ...publicSecondaryButtonSx,
            minHeight: 46,
          }}
        >
          Try again
        </Button>
      </Container>
    );
  }

  if (!history) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="info"
          sx={{
            borderRadius: '20px',
            border: `1px solid ${alpha(palette.heroBlueMid, 0.14)}`,
          }}
        >
          This case could not be found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mb: 3,
          p: { xs: 3, sm: 3.5, md: 4.5 },
          borderRadius: { xs: '28px', md: '34px' },
          background:
            'linear-gradient(140deg, rgba(18, 61, 103, 0.98) 0%, rgba(21, 83, 129, 0.97) 54%, rgba(49, 182, 228, 0.93) 100%)',
          color: palette.white,
          boxShadow: '0 34px 84px rgba(18, 61, 103, 0.16)',
        }}
      >
        <Box
          component="img"
          src={marketingSymbols.silhouette}
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            right: { xs: -16, md: 24 },
            top: { xs: -24, md: -46 },
            width: { xs: 160, md: 260 },
            opacity: 0.14,
            pointerEvents: 'none',
          }}
        />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 3, md: 4 }}
          justifyContent="space-between"
          alignItems={{ md: 'flex-start' }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Button
              component={RouterLink}
              to="/histories"
              startIcon={<ArrowBackRoundedIcon />}
              sx={{
                mb: 2.25,
                minHeight: 40,
                px: 1.8,
                color: palette.white,
                textTransform: 'none',
                fontWeight: 800,
                fontFamily: publicBodyFontFamily,
                borderRadius: '999px',
                bgcolor: alpha('#ffffff', 0.12),
                '&:hover': {
                  bgcolor: alpha('#ffffff', 0.18),
                },
              }}
            >
              Back to cases
            </Button>

            <Typography
              sx={{
                mb: 0.8,
                fontSize: '0.82rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: alpha('#ffffff', 0.76),
                fontFamily: publicBodyFontFamily,
              }}
            >
              Case review
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: publicHeadingFontFamily,
                fontSize: { xs: '2.15rem', md: '3rem' },
                lineHeight: 1.02,
                letterSpacing: '-0.04em',
                wordBreak: 'break-word',
              }}
            >
              {history.title || 'Untitled case'}
            </Typography>
            <Typography
              sx={{
                mt: 1.1,
                maxWidth: 700,
                color: alpha('#ffffff', 0.84),
                fontSize: { xs: '1rem', md: '1.08rem' },
                lineHeight: 1.7,
                fontFamily: publicBodyFontFamily,
              }}
            >
              Review section-by-section feedback, scoring, and supporting resources from this completed consultation.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mt: 2.5 }}>
              <Chip
                icon={<CalendarMonthRoundedIcon sx={{ color: 'inherit !important' }} />}
                label={formatDate(history.completedAt)}
                sx={{
                  bgcolor: alpha('#ffffff', 0.12),
                  color: palette.white,
                  borderRadius: '999px',
                  fontWeight: 700,
                  fontFamily: publicBodyFontFamily,
                }}
              />
              <Chip
                icon={<SchoolRoundedIcon sx={{ color: 'inherit !important' }} />}
                label={sessionModeLabel}
                sx={{
                  bgcolor: alpha('#ffffff', 0.12),
                  color: palette.white,
                  borderRadius: '999px',
                  fontWeight: 700,
                  fontFamily: publicBodyFontFamily,
                }}
              />
              {duration !== null ? (
                <Chip
                  icon={<TimerRoundedIcon sx={{ color: 'inherit !important' }} />}
                  label={`${duration} minute consultation`}
                  sx={{
                    bgcolor: alpha('#ffffff', 0.12),
                    color: palette.white,
                    borderRadius: '999px',
                    fontWeight: 700,
                    fontFamily: publicBodyFontFamily,
                  }}
                />
              ) : null}
            </Stack>
          </Box>

          <Stack spacing={1.5} sx={{ minWidth: { md: 220 }, width: { xs: '100%', md: 'auto' } }}>
            <Box
              sx={{
                borderRadius: '24px',
                p: 2.3,
                bgcolor: alpha('#ffffff', 0.12),
                border: `1px solid ${alpha('#ffffff', 0.14)}`,
              }}
            >
              <Typography
                sx={{
                  color: alpha('#ffffff', 0.76),
                  fontSize: '0.82rem',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontFamily: publicBodyFontFamily,
                }}
              >
                Overall score
              </Typography>
              <Typography
                sx={{
                  mt: 0.7,
                  fontSize: { xs: '2rem', md: '2.3rem' },
                  lineHeight: 1,
                  fontWeight: 900,
                  fontFamily: publicHeadingFontFamily,
                  letterSpacing: '-0.04em',
                }}
              >
                {(Number(history.overallPercentage) || 0).toFixed(1)}%
              </Typography>
            </Box>
            <Button
              onClick={handleExportPdf}
              disabled={exporting}
              startIcon={<DownloadRoundedIcon />}
              sx={{
                ...publicPrimaryButtonSx,
                minHeight: 48,
                bgcolor: palette.white,
                color: palette.heroBlueDeep,
                '&:hover': {
                  bgcolor: alpha('#ffffff', 0.92),
                },
              }}
            >
              {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {actionError ? (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: '20px',
            border: `1px solid ${alpha('#b42318', 0.12)}`,
          }}
        >
          {actionError}
        </Alert>
      ) : null}

      {scoreCards.length > 0 ? (
        <Grid container spacing={2.2} sx={{ mb: 3 }}>
          {scoreCards.map((card) => (
            <Grid key={card.key} item xs={12} sm={6} lg={12 / Math.min(scoreCards.length, 4)}>
              <MetricCard icon={card.icon} label={card.label} value={card.value} />
            </Grid>
          ))}
        </Grid>
      ) : null}

      <Stack spacing={3}>
        <SectionCard
          eyebrow="Feedback"
          title="Summary feedback"
          description="A concise overview of your performance in this consultation."
        >
          <Typography
            sx={{
              color: palette.inkSoft,
              lineHeight: 1.8,
              fontFamily: publicBodyFontFamily,
            }}
          >
            {history.summaryComments || 'No summary feedback was provided for this case.'}
          </Typography>
        </SectionCard>

        <SectionCard
          eyebrow="Clinical history"
          title="Section feedback"
          description="Use the horizontal tabs to review the marking criteria and feedback for each part of the consultation."
          action={selectedSection ? (
            <Chip
              label={`${selectedSection.questions.length} topics`}
              sx={{
                bgcolor: alpha(palette.heroBlueMid, 0.10),
                color: palette.heroBlueDeep,
                borderRadius: '999px',
                fontWeight: 800,
                fontFamily: publicBodyFontFamily,
              }}
            />
          ) : null}
        >
          {clinicalSections.length ? (
            <>
              <Box
                sx={{
                  mb: 2.5,
                  borderBottom: `1px solid ${alpha(palette.ink, 0.08)}`,
                }}
              >
                <Tabs
                  value={selectedTab}
                  onChange={(_, value) => setSelectedTab(value)}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 58,
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: '999px',
                      bgcolor: palette.heroBlueMid,
                    },
                    '& .MuiTabs-scrollButtons': {
                      color: palette.heroBlueDeep,
                    },
                  }}
                >
                  {clinicalSections.map((section) => (
                    <Tab
                      key={section.key}
                      label={section.title}
                      sx={{
                        minHeight: 58,
                        px: 2.25,
                        alignItems: 'flex-start',
                        color: palette.inkSoft,
                        fontSize: { xs: '0.76rem', md: '0.8rem' },
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        fontFamily: publicBodyFontFamily,
                        '&.Mui-selected': {
                          color: palette.heroBlueDeep,
                        },
                      }}
                    />
                  ))}
                </Tabs>
              </Box>

              {selectedSection ? (
                <Stack spacing={2}>
                  <Typography
                    sx={{
                      color: palette.ink,
                      fontSize: { xs: '1.05rem', md: '1.18rem' },
                      fontWeight: 800,
                      fontFamily: publicBodyFontFamily,
                    }}
                  >
                    {selectedSection.title}
                  </Typography>
                  <HistoryQuestionsTable questions={selectedSection.questions} />
                </Stack>
              ) : null}
            </>
          ) : (
            <EmptySectionState message="No section-level feedback is available for this case yet." />
          )}
        </SectionCard>

        {showFlowAssessment ? (
          <SectionCard
            eyebrow="Communication"
            title="Flow assessment"
            description="How clearly and naturally the consultation moved from one topic to the next."
          >
            <FlowAssessmentTable flowAssessment={history.flowAssessment} />
          </SectionCard>
        ) : null}

        {hasExaminerQuestions ? (
          <SectionCard
            eyebrow="Examiner"
            title="Examiner questions"
            description="Follow-up questioning and scored responses linked to this case."
          >
            <ExaminerQuestionsTable questions={history.examinerQuestions} onOpenMedia={handleOpenMedia} />
          </SectionCard>
        ) : null}

        {showEmpathyAssessment ? (
          <SectionCard
            eyebrow="Communication"
            title="Empathy assessment"
            description="Feedback on how the consultation felt from the patient perspective."
            action={(
              <ScoreChip
                score={history.empathyAssessment?.score}
                maxPoints={history.empathyAssessment?.maxPoints}
              />
            )}
          >
            <Box
              sx={{
                borderRadius: '22px',
                border: `1px solid ${alpha(palette.ink, 0.08)}`,
                bgcolor: alpha(palette.heroBlueMist, 0.55),
                p: 2.4,
              }}
            >
              <Typography
                sx={{
                  color: palette.inkSoft,
                  lineHeight: 1.8,
                  fontFamily: publicBodyFontFamily,
                }}
              >
                {history.empathyAssessment?.feedback || 'No empathy feedback was recorded.'}
              </Typography>
            </Box>
          </SectionCard>
        ) : null}

        {hasLearningContent ? (
          <SectionCard
            eyebrow="Learning"
            title="Learning resources"
            description="Supporting notes, links, and attachments connected to this case."
          >
            <Stack spacing={2.2}>
              {learningResources.summary?.trim() ? (
                <Box
                  sx={{
                    borderRadius: '22px',
                    border: `1px solid ${alpha(palette.ink, 0.08)}`,
                    bgcolor: alpha(palette.heroBlueMist, 0.55),
                    p: 2.4,
                  }}
                >
                  <Typography
                    sx={{
                      color: palette.ink,
                      fontWeight: 800,
                      mb: 0.7,
                      fontFamily: publicBodyFontFamily,
                    }}
                  >
                    Key learning points
                  </Typography>
                  <Typography
                    sx={{
                      color: palette.inkSoft,
                      lineHeight: 1.8,
                      whiteSpace: 'pre-line',
                      fontFamily: publicBodyFontFamily,
                    }}
                  >
                    {learningResources.summary}
                  </Typography>
                </Box>
              ) : null}

              {Array.isArray(learningResources.links) && learningResources.links.length > 0 ? (
                <Box>
                  <Typography
                    sx={{
                      color: palette.ink,
                      fontWeight: 800,
                      mb: 1.1,
                      fontFamily: publicBodyFontFamily,
                    }}
                  >
                    External resources
                  </Typography>
                  <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
                    {learningResources.links.map((resource) => (
                      <Button
                        key={resource.id || resource.url}
                        component="a"
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<LinkRoundedIcon />}
                        sx={{
                          ...publicSecondaryButtonSx,
                          minHeight: 44,
                        }}
                      >
                        {resource.label || resource.url}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              {Array.isArray(learningResources.attachments) && learningResources.attachments.length > 0 ? (
                <Box>
                  <Typography
                    sx={{
                      color: palette.ink,
                      fontWeight: 800,
                      mb: 1.1,
                      fontFamily: publicBodyFontFamily,
                    }}
                  >
                    Attachments
                  </Typography>
                  <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
                    {learningResources.attachments.map((file, index) => {
                      const url = resolveAttachmentUrl(file);
                      if (!url) {
                        return null;
                      }

                      const isPdf = file?.mimetype === 'application/pdf' || String(file?.originalName || '').toLowerCase().endsWith('.pdf');
                      const isImage = file?.mimetype?.startsWith('image/');

                      return (
                        <Button
                          key={file?.path || file?.url || `${file?.originalName}-${index}`}
                          component="a"
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={isPdf ? <PictureAsPdfRoundedIcon /> : isImage ? <ImageOutlinedIcon /> : <AttachFileRoundedIcon />}
                          endIcon={<OpenInNewRoundedIcon />}
                          sx={{
                            ...publicSecondaryButtonSx,
                            minHeight: 44,
                          }}
                        >
                          {file?.originalName || file?.filename || 'Open attachment'}
                        </Button>
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          </SectionCard>
        ) : null}

        {history.transcript ? (
          <SectionCard
            eyebrow="Conversation"
            title="Transcript"
            description="The full conversation transcript captured for this consultation."
          >
            <Box
              sx={{
                borderRadius: '22px',
                border: `1px solid ${alpha(palette.ink, 0.08)}`,
                bgcolor: alpha(palette.heroBlueMist, 0.55),
                p: { xs: 2, md: 2.4 },
                maxHeight: 560,
                overflowY: 'auto',
              }}
            >
              <Typography
                component="pre"
                sx={{
                  m: 0,
                  color: palette.ink,
                  fontSize: '0.9rem',
                  lineHeight: 1.75,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: '"IBM Plex Mono", "Consolas", monospace',
                }}
              >
                {history.transcript}
              </Typography>
            </Box>
          </SectionCard>
        ) : null}
      </Stack>

      <MediaViewerDialog
        open={mediaDialog.open}
        onClose={handleCloseMedia}
        src={mediaDialog.src}
        title={mediaDialog.title}
        mimetype={mediaDialog.mimetype}
      />
    </Container>
  );
}

export default HistoryDetailPage;
